import { useState, useEffect, useRef } from 'react';
import type { Task, TaskLog } from '@cowork-gen/core';
import { api, wsClient } from '../api/client';

interface LogViewerProps {
  task: Task | null;
}

export function LogViewer({ task }: LogViewerProps) {
  const [logs, setLogs] = useState<TaskLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!task) {
      setLogs([]);
      return;
    }

    loadLogs();

    // Subscribe to real-time updates
    const unsubscribe = wsClient.on('log', (data) => {
      const logData = data as { taskId: string; logs: string[] };
      if (logData.taskId === task.id) {
        const newLogs: TaskLog[] = logData.logs.map((content, i) => ({
          id: Date.now() + i,
          taskId: task.id,
          iteration: task.iterations,
          logType: 'output' as const,
          content,
          createdAt: new Date(),
        }));
        setLogs((prev) => [...prev, ...newLogs]);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [task?.id]);

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  async function loadLogs() {
    if (!task) return;

    setLoading(true);
    try {
      const response = await api.getTaskLogs(task.id);
      if (response.success && response.data) {
        setLogs(response.data);
      }
    } catch (err) {
      console.error('Failed to load logs:', err);
    } finally {
      setLoading(false);
    }
  }

  if (!task) {
    return (
      <div className="bg-white rounded-lg shadow h-full flex items-center justify-center text-gray-500">
        Select a task to view logs
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow h-full flex flex-col">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Logs: {task.storyId}
          </h2>
          <p className="text-sm text-gray-500">
            Iteration {task.iterations}/{task.maxIterations}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              className="rounded"
            />
            Auto-scroll
          </label>
          <button
            onClick={loadLogs}
            disabled={loading}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto p-4 font-mono text-sm bg-gray-900 text-gray-100 log-viewer"
      >
        {logs.length === 0 ? (
          <div className="text-gray-500">No logs yet</div>
        ) : (
          logs.map((log) => (
            <div
              key={log.id}
              className={`mb-2 ${getLogTypeColor(log.logType)}`}
            >
              <span className="text-gray-500">
                [{new Date(log.createdAt).toLocaleTimeString()}]
                [{log.logType}]
                {log.iteration !== undefined && ` [iter ${log.iteration + 1}]`}
              </span>
              <pre className="whitespace-pre-wrap mt-1">{log.content}</pre>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function getLogTypeColor(logType: TaskLog['logType']): string {
  switch (logType) {
    case 'error':
      return 'text-red-400';
    case 'diff':
      return 'text-green-400';
    case 'info':
      return 'text-blue-400';
    default:
      return 'text-gray-100';
  }
}
